import { mapGetters } from "vuex";
import axios from "@/axios";
import Vue from "vue";

const mixin = {
  install(Vue) {
    Vue.mixin({
      methods: {
        // Define your universal function
        encryptIt(string) {
          const CryptoJS = require("crypto-js");
          const secretKey = CryptoJS.enc.Utf8.parse(
            "12345678123456781234567812345678"
          );
          const iv = CryptoJS.enc.Utf8.parse("Ef7ix7ETPgghl3vP");
          let encrypted = CryptoJS.AES.encrypt(
            CryptoJS.enc.Utf8.parse(string),
            secretKey,
            { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
          ).toString();
          return encrypted.replace(/[+]/g, "-").replace(/[/]/g, "_").replace(/[=]/g, ".");
        },
        decryptIt(string) {
          const CryptoJS = require("crypto-js");
          const secretKey = CryptoJS.enc.Utf8.parse(
            "12345678123456781234567812345678"
          );
          const iv = CryptoJS.enc.Utf8.parse("Ef7ix7ETPgghl3vP");
          string = string.replace(/[-]/g, "+").replace(/[_]/g, "/").replace(/[.]/g, "=");
          let encrypted = CryptoJS.AES.decrypt(string, secretKey, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
          }).toString(CryptoJS.enc.Utf8);
          return encrypted;
        },
        async saveUserTimestamp(old_json, new_json) {
          try {
            let form = {
              from_path: this.$route.path,
              from_name: this.$route.name,
              user_id: this.user.id,
              old_json: old_json ? JSON.stringify(old_json) : null,
              new_json: JSON.stringify(new_json),
            };
            await axios.post(`/user_timestamps`, form);
          } catch (error) {
            console.log(error);
          }
        },
        isDeletePage() {
          let response = false;
          if (this.$route.query.open_trash) {
            response = true;
          }
          return response;
        },
        
        // Permission checking methods
        can(permission) {
          if (!permission) return false;
          
          // Super Admin and Admin have all permissions
          if (this.isAdmin || this.isSuperAdmin) {
            return true;
          }
          
          // Guest users have limited permissions
          if (this.isGuestUser) {
            const guestAllowedPermissions = [
              'PRODUCTS.VIEW',
              'PRODUCTS.SINGLE_VIEW',
              'PROFILE.VIEW'
            ];
            return guestAllowedPermissions.includes(permission);
          }
          
          // Check if user has the specific permission
          return this.permissions && this.permissions.includes(permission);
        },
        
        canAny(permissions) {
          if (!permissions || !Array.isArray(permissions)) return false;
          
          // Super Admin and Admin have all permissions
          if (this.isAdmin || this.isSuperAdmin) {
            return true;
          }
          
          // Check if user has any of the permissions
          return permissions.some(permission => this.can(permission));
        },
        
        canAll(permissions) {
          if (!permissions || !Array.isArray(permissions)) return false;
          
          // Super Admin and Admin have all permissions
          if (this.isAdmin || this.isSuperAdmin) {
            return true;
          }
          
          // Check if user has all of the permissions
          return permissions.every(permission => this.can(permission));
        },
        
        // Convenience methods for common permission patterns
        canView(module) {
          return this.can(`${module.toUpperCase()}.VIEW`);
        },
        
        canCreate(module) {
          return this.can(`${module.toUpperCase()}.CREATE`);
        },
        
        canUpdate(module) {
          return this.can(`${module.toUpperCase()}.UPDATE`);
        },
        
        canDelete(module) {
          return this.can(`${module.toUpperCase()}.DELETE`);
        },
        
        canViewSingle(module) {
          return this.can(`${module.toUpperCase()}.SINGLE_VIEW`);
        },
        
        // Healthcare-specific convenience methods
        canAccessHealthcare() {
          return this.canAny([
            'APPOINTMENTS.VIEW',
            'CONSULTATIONS.VIEW',
            'PRESCRIPTIONS.VIEW',
            'PATIENTS.VIEW'
          ]) || this.hasRole(['DOCTOR', 'ADMIN', 'SUPER ADMIN']);
        },
        
        canManagePatients() {
          return this.canAny([
            'USERS.CREATE',
            'USERS.UPDATE',
            'USERS.VIEW'
          ]) && !this.isGuestUser;
        },
        
        canConductConsultations() {
          return this.can('CONSULTATIONS.CREATE') || this.hasRole('DOCTOR');
        },
        
        canPrescribeMedicines() {
          return this.can('PRESCRIPTIONS.CREATE') || this.hasRole('DOCTOR');
        },
        
        canBookAppointments() {
          return this.can('APPOINTMENTS.CREATE') || !this.isGuestUser;
        },
        
        // Role checking methods
        hasRole(roles) {
          if (!this.roleName) return false;
          if (Array.isArray(roles)) {
            return roles.includes(this.roleName);
          }
          return this.roleName === roles;
        },
      },
      // Toast notification methods (using console for now, should be replaced with proper toast library)
      created() {
        if (!this.$toast) {
          this.$toast = {
            success: (message) => {
              console.log('SUCCESS:', message);
              // You can replace this with proper toast implementation
              // For now, using console.log as a placeholder
            },
            error: (message) => {
              console.error('ERROR:', message);
              // You can replace this with proper toast implementation
            },
            info: (message) => {
              console.info('INFO:', message);
              // You can replace this with proper toast implementation
            }
          };
        }
      },
      computed: {
        ...mapGetters({
          authenticated: "auth/authenticated",
          user: "auth/user",
          company: "auth/company",
          errors: "errors/getErrors",
          status: "errors/getStatus",
          baseUrl: "settings/getBaseUrl",
          mediaUrl: "settings/getMediaUrl",
          rowsPerPage: "settings/getRowsPerPage",
          roleName: "auth/roleName",
          permissions: "auth/permissions",
          userPosition: "auth/userPosition",
          isAdmin: "auth/isAdmin",
          isSuperAdmin: "auth/isSuperAdmin",
        }),
        
        // Additional computed properties for permission system
        isGuestUser() {
          return this.user && this.user.is_guest === true;
        },
        
        userRole() {
          return this.roleName;
        },
      },
    });
  },
};

Vue.use(mixin);

export default mixin;
